import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import L from 'leaflet';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import type { GroupSummaryResponse } from '@tabita/contracts';
import { useI18n } from '@/i18n/i18n-provider';
import { groupsApi } from '@/services/groups.api';
import { ApiError } from '@/services/api-client';
import { Alert, EmptyState, Spinner } from '@/components/ui';

/**
 * Not `L.Icon.Default.mergeOptions(...)` — see `location-map-picker.tsx`'s
 * own comment on why: `Icon.Default._getIconUrl` prepends an
 * auto-detected `imagePath` even after `mergeOptions` overrides `iconUrl`,
 * doubling a bundler-resolved URL onto itself. A plain `L.icon(...)`,
 * passed per `<Marker>`, sidesteps that entirely.
 */
const pinIcon = L.icon({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41],
});

/** Santo Domingo — same reasonable default `LocationMapPicker` opens on
 *  when nothing has a location set yet. */
const DEFAULT_CENTER: [number, number] = [18.4861, -69.9312];
const DEFAULT_ZOOM = 12;

interface PinnedGroup extends GroupSummaryResponse {
  readonly latitude: number;
  readonly longitude: number;
}

function hasLocation(group: GroupSummaryResponse): group is PinnedGroup {
  return group.latitude !== null && group.longitude !== null;
}

/**
 * Every group with a pinned location, on one map — the read-only counterpart
 * to the picker each group's own detail page uses to set that pin.
 *
 * A plain list of a hundred cells says nothing about which neighborhoods the
 * church actually reaches; this is the view that does.
 */
export function GroupsMapPage(): ReactNode {
  const { t } = useI18n();

  const query = useQuery({
    queryKey: ['groups', 'map'],
    // 100 is the API's own ceiling on `limit` — a church with more groups than
    // that sees its first hundred, not a silent truncation to worry about yet.
    queryFn: ({ signal }) => groupsApi.list({ limit: 100 }, signal),
  });

  const pinned = useMemo(() => (query.data?.data ?? []).filter(hasLocation), [query.data]);

  const first = pinned[0];
  const center: [number, number] = first ? [first.latitude, first.longitude] : DEFAULT_CENTER;

  return (
    <div className="page">
      <Link to="/groups" className="back-link">
        {t('common.actions.back')}
      </Link>

      <header className="page-header">
        <div>
          <h1 className="page-title">{t('groups.group.mapTitle')}</h1>
          {query.data && (
            <p className="page-count">{t('groups.group.count', { count: pinned.length })}</p>
          )}
        </div>
      </header>

      {query.isError && (
        <Alert tone="danger">
          {query.error instanceof ApiError
            ? t(`errors.${query.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      {query.isPending && <Spinner label={t('common.states.loading')} />}

      {query.isSuccess && pinned.length === 0 && <EmptyState title={t('groups.group.mapEmpty')} />}

      {pinned.length > 0 && (
        <div className="groups-map-container">
          <MapContainer
            center={center}
            zoom={DEFAULT_ZOOM}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {pinned.map((group) => (
              <Marker key={group.id} position={[group.latitude, group.longitude]} icon={pinIcon}>
                <Popup>
                  <Link to={`/groups/${group.id}`}>{group.name}</Link>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      )}
    </div>
  );
}
